
import { getSharedAudioContext } from './memoryService';

export interface AudioQualityReport {
  status: 'OK' | 'WARN' | 'REJECT';
  reasonCode: string;
  message: string;
  details?: {
    container: string;
    sampleRate?: number;
    duration?: number;
    bitrateKbps?: number;
  }
}

const MIN_DURATION_SEC = 3;
const MIN_SAMPLERATE_HZ = 44100;
const WARN_BITRATE_KBPS = 128;

/**
 * Analyse la qualité d'un fichier audio avant de l'envoyer au DSP.
 * Le principe est de valider la "qualité minimale" plutôt que le format.
 */
export async function analyzeAudioQuality(file: File): Promise<AudioQualityReport> {
    if (!file) {
        return { status: 'REJECT', reasonCode: 'NO_FILE', message: 'Aucun fichier sélectionné.' };
    }

    const extension = file.name.split('.').pop()?.toLowerCase() || '';

    // Règle 1: Fichier trop petit pour être pertinent
    if (file.size < 100 * 1024) { // 100 KB
        return { status: 'REJECT', reasonCode: 'FILE_TOO_SMALL', message: `Fichier audio trop court ou corrompu (${(file.size/1024).toFixed(0)}KB).` };
    }

    try {
        const audioCtx = await getSharedAudioContext();
        const arrayBuffer = await file.arrayBuffer();
        const audioBuffer = await audioCtx.decodeAudioData(arrayBuffer.slice(0)); // slice(0) to create a copy

        const { duration, sampleRate, numberOfChannels } = audioBuffer;
        const bitrateKbps = Math.round((file.size * 8) / duration / 1000);

        // Règle 2: Durée minimale
        if (duration < MIN_DURATION_SEC) {
            return { status: 'REJECT', reasonCode: 'DURATION_TOO_SHORT', message: `L'audio doit faire plus de ${MIN_DURATION_SEC} secondes.` };
        }

        // Règle 3: Qualité d'échantillonnage (Qualité CD minimum)
        if (sampleRate < MIN_SAMPLERATE_HZ) {
            return { status: 'REJECT', reasonCode: 'LOW_SAMPLERATE', message: `Qualité audio insuffisante (${sampleRate}Hz). Minimum requis : ${MIN_SAMPLERATE_HZ}Hz.` };
        }
        
        const details = { container: extension, sampleRate, duration, bitrateKbps };

        // Règle 4: WebM est le format natif du Studio GSC (MediaRecorder).
        // On le valide comme Optimal sans vérifier le bitrate (VBR souvent mal estimé).
        if (extension === 'webm') {
            return { status: 'OK', reasonCode: 'NATIVE_FORMAT', message: 'Format Studio Standard (WebM).', details };
        }

        // Règle 5: Bitrate faible pour les formats compressés non-natifs (MP3/AAC/M4A)
        if (['mp4', 'm4a', 'aac', 'mp3'].includes(extension) && bitrateKbps < WARN_BITRATE_KBPS) {
             return { status: 'WARN', reasonCode: 'LOW_BITRATE', message: `Compression audio élevée détectée (${bitrateKbps}kbps). L'analyse peut être moins précise.`, details };
        }

        // Si tout passe, c'est OK
        return { status: 'OK', reasonCode: 'VALID_AUDIO', message: 'Qualité audio OK.', details };

    } catch (e) {
        // Erreur de décodage = Format non supporté
        return { status: 'REJECT', reasonCode: 'DECODING_ERROR', message: 'Fichier audio non compatible ou corrompu. Utilisez un enregistrement WAV ou MP4 de bonne qualité.' };
    }
}
